import { useCallback } from "react";
import { api } from "@/lib/api";
import { bustCachePrefix } from "@/lib/useCachedFetch";

// sessionStorage cache prefixes that snapshot the applicant's pinned
// courses (Shortlist page + Compare page). Any mutation to
// `/api/shortlist` must invalidate BOTH so subsequent visits don't
// hydrate a stale empty list. Kept as constants (not string literals
// inline) so a rename never drifts across call sites.
export const SHORTLIST_CACHE_PREFIX = "wua:shortlist:";
export const COMPARE_CACHE_PREFIX = "wua:compare:";

function _bustShortlistCaches() {
  bustCachePrefix(SHORTLIST_CACHE_PREFIX);
  bustCachePrefix(COMPARE_CACHE_PREFIX);
}

/**
 * useShortlistWriter
 * -----------------------------------------------------------------------------
 * Thin wrapper over the `/api/shortlist` mutation endpoints that
 * guarantees the sessionStorage snapshots powering `/shortlist` and
 * `/compare` are invalidated on every write. Prevents the class of bug
 * where a user pins a course from Advanced / Quick Check / Uni Detail
 * and then sees an empty shortlist page because that page hydrated
 * from a pre-mutation cache.
 *
 * Returned functions:
 *
 *   addToShortlist({ university_id, degree_id? })
 *     → POST /shortlist, then bust caches. Throws on failure so callers
 *       can undo their optimistic UI update.
 *
 *   removeFromShortlist({ university_id, degree_id? })
 *     → DELETE /shortlist/{uni}[?degree_id=…], then bust caches.
 *
 *   bustShortlistCaches()
 *     → Manual escape hatch for callers that POST via other means
 *       (e.g. the AuthCallback batch-replay loop) but still need the
 *       cache guarantee. Prefer the write helpers where possible.
 *
 * All three are memoised with `useCallback` so consumers can safely
 * pass them into effects / event handlers without triggering rerenders.
 */
export function useShortlistWriter() {
  const addToShortlist = useCallback(async ({ university_id, degree_id }) => {
    const payload = { university_id };
    if (degree_id) payload.degree_id = degree_id;
    const res = await api.post("/shortlist", payload);
    _bustShortlistCaches();
    return res.data;
  }, []);

  const removeFromShortlist = useCallback(async ({ university_id, degree_id }) => {
    const q = degree_id ? `?degree_id=${encodeURIComponent(degree_id)}` : "";
    const res = await api.delete(`/shortlist/${university_id}${q}`);
    _bustShortlistCaches();
    return res.data;
  }, []);

  const bustShortlistCaches = useCallback(() => {
    _bustShortlistCaches();
  }, []);

  return { addToShortlist, removeFromShortlist, bustShortlistCaches };
}
